import styled, { StyledComponent } from '@emotion/styled';
import React, { FunctionComponent, HTMLAttributes } from 'react';

import { ITheme, themed } from '../../themer';

export type IIconVariation =
	| 'inverted'
	| 'highlight'
	| 'brand'
	| 'text'
	| 'error';

export interface IIconStyleProps {
	variation?: IIconVariation;
	alt?: string;
}

export type IApplyColorToProps = ('fill' | 'stroke')[];

export interface IWithIconStylesHoC {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(Icon: any, style?: IApplyColorToProps): StyledComponent<
		{},
		IIconStyleProps & HTMLAttributes<HTMLOrSVGImageElement>,
		ITheme
	>;
}

function getStylesForProps(props: string[], value: string) {
	return props.map((prop) => `${prop}: ${value};`).join('\n');
}

function getColorForVariation(applyColorToProps: IApplyColorToProps) {
	return (
		{ color }: ITheme,
		{ variation }: IIconStyleProps & HTMLAttributes<HTMLOrSVGImageElement>,
	) => {
		switch (variation) {
			case 'inverted':
				return getStylesForProps(applyColorToProps, color.text.inverted);

			case 'highlight':
				return getStylesForProps(applyColorToProps, color.highlight);

			case 'brand':
				return getStylesForProps(applyColorToProps, color.brand);

			case 'text':
				return getStylesForProps(applyColorToProps, color.text.default);

			case 'error':
				return getStylesForProps(applyColorToProps, color.state.error);

			default:
				// Use the original color as defined in the SVG by default.
				return getStylesForProps(applyColorToProps, color.secondary);
		}
	};
}

export const withIconStyles: IWithIconStylesHoC = (
	Icon: FunctionComponent<React.SVGProps<SVGSVGElement>>,
	applyColorToProps = ['fill'],
) => {
	const IconWithRole = (props: IIconStyleProps) => (
		<Icon role="img" {...props} />
	);

	return styled(IconWithRole)<
		IIconStyleProps & HTMLAttributes<HTMLOrSVGImageElement>,
		ITheme
	>`
		vertical-align: middle;

		g,
		path,
		rect {
			${themed(getColorForVariation(applyColorToProps))};
		}
	`;
};
